import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronLeft, MessageSquare, Eye, Trash2, RefreshCw, ChevronRight, AlertTriangle, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { useToast } from '../ui/use-toast';
import { feedbackService } from '@/services/feedbackService';
import { Feedback, AppName, FeedbackType } from '@/types/feedback';

interface FeedbackManagementSectionProps {
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
}

interface FeedbackSummary {
  appName: AppName;
  totalCount: number;
  bugCount: number;
  featureCount: number;
  feedbackCount: number;
  unreadCount: number;
}

interface FeedbackStats {
  total: number;
  byApp: { [key in AppName]: number };
  byType: { [key in FeedbackType]: number };
}

const FeedbackManagementSection: React.FC<FeedbackManagementSectionProps> = ({
  isExpanded,
  setIsExpanded
}) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [feedbackSummary, setFeedbackSummary] = useState<FeedbackSummary[]>([]);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback[]>([]);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState<AppName | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState<Feedback | null>(null);
  const [isMigrating, setIsMigrating] = useState(false);
  const [lastVisibleDocs, setLastVisibleDocs] = useState<any[]>([]); // Stack for pagination cursors
  const [hasMore, setHasMore] = useState(false);
  
  const [stats, setStats] = useState<FeedbackStats>({
    total: 0,
    byApp: { 'Employee Directory': 0, 'Good Thing Tracker': 0, 'Day Away Board': 0, 'SOP Central': 0, 'Idea Machine': 0, 'JLS PLMS': 0 },
    byType: { 'Bug Report': 0, 'Feature Request': 0, 'Feedback': 0 }
  });

  const loadFeedbackSummary = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Use getFeedbackStats for efficient stats loading instead of loading everything
      const statsData = await feedbackService.getFeedbackStats();
      
      // Also get counts per app efficiently if possible, or fall back to array length via stats
      // Since we don't have a direct "stats per app" breakdown that perfectly matches FeedbackSummary
      // we might need to approximate or update FeedbackService to return this.
      // But for now, we'll use the existing stats structure.
      
      // NOTE: To get accurate counts per type per app without loading ALL docs, we would need aggregation queries.
      // For now, we will maintain the existing logic of loading all feedback for the summary view
      // but warn that this doesn't scale.
      // In a true scalable solution, we would maintain counters in Firestore or use aggregation queries.
      
      // Let's use getAllFeedback for now as the summary view depends on full aggregation,
      // but we've optimized the individual app view below.
      const allFeedback = await feedbackService.getAllFeedback();
      
      // Group by app
      const appGroups: { [key in AppName]: Feedback[] } = {
        'Employee Directory': [],
        'Good Thing Tracker': [],
        'Day Away Board': [],
        'SOP Central': [],
        'Idea Machine': [],
        'JLS PLMS': []
      };

      allFeedback.forEach((item: Feedback) => {
        if (appGroups[item.appName]) {
          appGroups[item.appName].push(item);
        }
      });

      // Create summary data
      const summary: FeedbackSummary[] = (Object.keys(appGroups) as AppName[]).map(appName => {
        const appFeedback = appGroups[appName];
        return {
          appName,
          totalCount: appFeedback.length,
          bugCount: appFeedback.filter(f => f.type === 'Bug Report').length,
          featureCount: appFeedback.filter(f => f.type === 'Feature Request').length,
          feedbackCount: appFeedback.filter(f => f.type === 'Feedback').length,
          unreadCount: 0
        };
      });

      setFeedbackSummary(summary);

      // Update stats
      const updatedStats: FeedbackStats = {
        total: allFeedback.length,
        byApp: {
          'Employee Directory': appGroups['Employee Directory'].length,
          'Good Thing Tracker': appGroups['Good Thing Tracker'].length,
          'Day Away Board': appGroups['Day Away Board'].length,
          'SOP Central': appGroups['SOP Central'].length,
          'Idea Machine': appGroups['Idea Machine'].length,
          'JLS PLMS': appGroups['JLS PLMS'].length
        },
        byType: {
          'Bug Report': allFeedback.filter((f: Feedback) => f.type === 'Bug Report').length,
          'Feature Request': allFeedback.filter((f: Feedback) => f.type === 'Feature Request').length,
          'Feedback': allFeedback.filter((f: Feedback) => f.type === 'Feedback').length
        }
      };

      setStats(updatedStats);
    } catch (error) {
      console.error('Error loading feedback summary:', error);
      toast({
        title: 'Error loading feedback',
        description: 'Failed to load feedback summary. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const loadAppFeedback = async (appName: AppName, page: number = 1, startAfterDoc: any = null) => {
    try {
      setIsLoading(true);
      const LIMIT = 10;
      
      const { feedback, lastVisible } = await feedbackService.getFeedbackPage(LIMIT, startAfterDoc, appName);
      
      setSelectedFeedback(feedback);
      setSelectedApp(appName);
      setCurrentPage(page);
      
      // Update pagination state
      setHasMore(feedback.length === LIMIT);
      
      if (page === 1) {
        setLastVisibleDocs([lastVisible]); // First page, just set the first cursor
      } else {
        // If moving forward, add to stack. If moving back, we handle in the navigation functions
        const newDocs = [...lastVisibleDocs];
        newDocs[page - 1] = lastVisible;
        setLastVisibleDocs(newDocs);
      }
      
      setIsDetailModalOpen(true);
    } catch (error) {
      console.error('Error loading app feedback:', error);
      toast({
        title: 'Error loading feedback',
        description: 'Failed to load feedback for this app.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (feedback: Feedback) => {
    setFeedbackToDelete(feedback);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!feedbackToDelete) return;

    try {
      setIsDeleting(feedbackToDelete.id);
      await feedbackService.deleteFeedback(feedbackToDelete.id);
      
      // Refresh data
      await loadFeedbackSummary();
      if (selectedApp) {
        await loadAppFeedback(selectedApp);
      }

      toast({
        title: 'Feedback deleted',
        description: 'The feedback has been successfully deleted.',
        variant: 'default'
      });
      
      setIsDeleteModalOpen(false);
      setFeedbackToDelete(null);
    } catch (error) {
      console.error('Error deleting feedback:', error);
      toast({
        title: 'Delete failed',
        description: 'Failed to delete feedback. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setFeedbackToDelete(null);
  };

  const getTypeColor = (type: FeedbackType): string => {
    switch (type) {
      case 'Bug Report': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'Feature Request': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Feedback': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const formatDate = (timestamp: Date | any): string => {
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Pagination helpers
  // In the new system, we show a list of items per page, not one item per page like before?
  // The previous implementation: "return selectedFeedback[currentPage - 1] || null;" suggests it was showing 1 item at a time from a full list?
  // Let's check the render:
  // "{/* Current Feedback */} {(() => { const feedback = getCurrentFeedback(); ... "
  // Yes, it was rendering a SINGLE feedback item at a time from the array.
  
  // NEW REQUIREMENT: "Implement "Load More" or "Next/Previous" buttons that fetch data in chunks"
  // If we want to keep the "Detail View" style (one item at a time), we need to fetch a chunk, then let user navigate through them.
  // OR, we change the UI to a List View. The instructions say "Refactor the feedback list".
  
  // BUT, looking at the existing UI code in the search block:
  // It renders a SINGLE item detail view. This is a "Detail Modal" after clicking "View Details".
  // If we change this to a list, we are changing the UX significantly.
  // Let's stick to the spirit of the instruction: "Refactor the feedback list to use the new paginated service method."
  // AND "Implement "Load More" buttons that fetch data in chunks".
  
  // OPTION A: Switch to List View inside the modal.
  // OPTION B: Keep Detail View but load chunks.
  
  // Given "Load More" typically implies a list, let's switch the Modal content to be a LIST of feedback items,
  // with ability to expand/view details.
  
  const handleNextPage = () => {
    if (selectedApp && hasMore) {
       // Get the cursor for the NEXT page.
       // lastVisibleDocs[currentPage - 1] holds the cursor for the END of current page.
       const cursor = lastVisibleDocs[currentPage - 1];
       loadAppFeedback(selectedApp, currentPage + 1, cursor);
    }
  };

  const handlePreviousPage = () => {
    if (selectedApp && currentPage > 1) {
       // We need the cursor for the page BEFORE the previous page to start at the beginning of previous page.
       // Or simpler: We maintain a stack of cursors.
       // To get page X, we need startAfter(cursor for page X-1).
       // Page 1: startAfter(null)
       // Page 2: startAfter(cursor1)
       // Page 3: startAfter(cursor2)
       
       // If we are on Page 2 and want Page 1: startAfter(null)
       // If we are on Page 3 and want Page 2: startAfter(cursor1)
       
       const previousPage = currentPage - 1;
       const cursorIndex = previousPage - 2; // -1 for 0-based, -1 for "previous page end"
       
       const cursor = cursorIndex >= 0 ? lastVisibleDocs[cursorIndex] : null;
       loadAppFeedback(selectedApp, previousPage, cursor);
    }
  };

  // Cleanup function to remove broken image URLs
  const cleanupBrokenImages = async () => {
    try {
      setIsMigrating(true);
      
      // Import Firebase modules
      const { db } = await import('../../config/firebase');
      const { collection, getDocs, doc, updateDoc } = await import('firebase/firestore');
      
      console.log('🚀 Starting broken image cleanup...');
      
      // Get all feedback documents
      const feedbackRef = collection(db, 'feedback');
      const querySnapshot = await getDocs(feedbackRef);
      
      let totalProcessed = 0;
      let totalCleaned = 0;
      
      for (const docSnapshot of querySnapshot.docs) {
        const data = docSnapshot.data();
        if (data.images && data.images.length > 0) {
          console.log(`🔄 Processing feedback ${docSnapshot.id} for ${data.appName}`);
          
          // Simply remove all image URLs since we know they don't exist
          const docRef = doc(db, 'feedback', docSnapshot.id);
          await updateDoc(docRef, {
            images: [], // Remove all broken image URLs
            cleanedAt: new Date()
          });
          
          totalCleaned += data.images.length;
          totalProcessed++;
          console.log(`✅ Cleaned feedback ${docSnapshot.id} - removed ${data.images.length} broken images`);
        }
      }
      
      toast({
        title: 'Cleanup completed',
        description: `Processed ${totalProcessed} feedback items, removed ${totalCleaned} broken image references.`,
        variant: 'default'
      });
      
      // Refresh the data
      await loadFeedbackSummary();
      
    } catch (error) {
      console.error('❌ Cleanup failed:', error);
      toast({
        title: 'Cleanup failed',
        description: 'There was an error cleaning up image URLs. Check console for details.',
        variant: 'destructive'
      });
    } finally {
      setIsMigrating(false);
    }
  };

  useEffect(() => {
    if (isExpanded) {
      loadFeedbackSummary();
    }
  }, [isExpanded, loadFeedbackSummary]);

  return (
    <>
      <div className="p-6 bg-white rounded-lg shadow-sm dark:bg-gray-800">
        <div className="flex items-center gap-2 mb-4 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
          <MessageSquare className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          <h2 className="text-xl font-semibold dark:text-white">User Feedback Management</h2>
          <Badge variant="secondary" className="ml-2">
            {stats.total} total
          </Badge>
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 ml-auto dark:text-gray-400" />
          ) : (
            <ChevronLeft className="h-5 w-5 ml-auto dark:text-gray-400" />
          )}
        </div>

        {isExpanded && (
          <div className="space-y-6">
            {/* Overall Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Feedback</div>
                <div className="text-2xl font-bold dark:text-white">{stats.total}</div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                <div className="text-sm text-red-600 dark:text-red-400">Bug Reports</div>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.byType['Bug Report']}</div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="text-sm text-blue-600 dark:text-blue-400">Feature Requests</div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.byType['Feature Request']}</div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <div className="text-sm text-green-600 dark:text-green-400">General Feedback</div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.byType['Feedback']}</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium dark:text-white">Feedback by Application</h3>
              <div className="flex items-center gap-2">
                <Button
                  onClick={cleanupBrokenImages}
                  disabled={isLoading || isMigrating}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Upload className={`h-4 w-4 ${isMigrating ? 'animate-spin' : ''}`} />
                  {isMigrating ? 'Cleaning...' : 'Clean Images'}
                </Button>
                <Button
                  onClick={loadFeedbackSummary}
                  disabled={isLoading}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>

            {/* App Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {feedbackSummary.map((summary) => (
                <div key={summary.appName} className="border dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium dark:text-white">{summary.appName}</h4>
                    {summary.unreadCount > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        {summary.unreadCount} unread
                      </Badge>
                    )}
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Total:</span>
                      <span className="font-medium dark:text-white">{summary.totalCount}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-red-600 dark:text-red-400">Bugs:</span>
                      <span className="font-medium text-red-600 dark:text-red-400">{summary.bugCount}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-blue-600 dark:text-blue-400">Features:</span>
                      <span className="font-medium text-blue-600 dark:text-blue-400">{summary.featureCount}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-green-600 dark:text-green-400">Feedback:</span>
                      <span className="font-medium text-green-600 dark:text-green-400">{summary.feedbackCount}</span>
                    </div>
                  </div>

                  <Button
                    onClick={() => loadAppFeedback(summary.appName)}
                    disabled={isLoading || summary.totalCount === 0}
                    className="w-full flex items-center gap-2"
                    variant="outline"
                  >
                    <Eye className="h-4 w-4" />
                    View Details
                  </Button>
                </div>
              ))}
            </div>

          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Feedback for {selectedApp}
              {selectedFeedback.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {selectedFeedback.length} items
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {selectedFeedback.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No feedback found for this application.
              </div>
            ) : (
              <>
                {/* Pagination Controls */}
                <div className="flex items-center justify-between border-b pb-4">
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      variant="outline"
                      size="sm"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Page {currentPage}
                    </span>
                    <Button
                      onClick={handleNextPage}
                      disabled={!hasMore}
                      variant="outline"
                      size="sm"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Feedback List */}
                <div className="space-y-4">
                  {selectedFeedback.map((feedback) => (
                    <div key={feedback.id} className="border rounded-lg p-4 dark:border-gray-700">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className={getTypeColor(feedback.type)}>
                            {feedback.type}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(feedback.timestamp)}
                          </span>
                          <Button
                            onClick={() => handleDeleteClick(feedback)}
                            disabled={isDeleting === feedback.id}
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                          >
                            {isDeleting === feedback.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>

                      <div className="mb-2">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          From: {feedback.userFullName} ({feedback.userEmail})
                        </p>
                      </div>

                      <div className="mb-3">
                        <p className="text-sm dark:text-gray-200 whitespace-pre-wrap">
                          {feedback.message}
                        </p>
                      </div>

                      {feedback.images && feedback.images.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                            Attachments ({feedback.images.length}):
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {feedback.images.map((url: string, index: number) => {
                              // Validate URL format
                              const isValidUrl = url && (url.startsWith('https://') || url.startsWith('http://'));
                              const isFirebaseStorageUrl = url && url.includes('firebasestorage.googleapis.com');
                              
                              return (
                                <div key={index} className="border rounded-lg p-2">
                                  <div className="relative">
                                    {isValidUrl ? (
                                      <img
                                        src={url}
                                        alt={`Attachment ${index + 1}`}
                                        className="w-full h-32 object-cover rounded cursor-pointer hover:opacity-80"
                                        onClick={() => {
                                          const newWindow = window.open('', '_blank');
                                          if (newWindow) {
                                            newWindow.document.write(`
                                              <html>
                                                <head><title>Feedback Image ${index + 1}</title></head>
                                                <body style="margin:0;padding:20px;background:#f0f0f0;display:flex;justify-content:center;align-items:center;min-height:100vh;">
                                                  <img src="${url}" style="max-width:100%;max-height:100%;object-fit:contain;" alt="Feedback Image ${index + 1}" />
                                                </body>
                                              </html>
                                            `);
                                            newWindow.document.close();
                                          }
                                        }}
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.style.display = 'none';
                                          const parent = target.parentElement;
                                          if (parent) {
                                            parent.innerHTML = `
                                              <div class="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded">
                                                <p class="text-xs text-red-500 mb-2">Error loading image</p>
                                                <a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:text-blue-700 text-xs underline">
                                                  Link
                                                </a>
                                              </div>
                                            `;
                                          }
                                        }}
                                      />
                                    ) : (
                                      <div className="w-full h-32 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center">
                                        <div className="text-center">
                                          <p className="text-xs text-red-500 mb-2">Invalid URL</p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setIsDetailModalOpen(false)} variant="outline">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Confirm Delete
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Are you sure you want to delete this feedback? This action cannot be undone.
            </p>
            {feedbackToDelete && (
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  From: {feedbackToDelete.userFullName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Type: {feedbackToDelete.type}
                </p>
                <p className="text-sm mt-1 truncate">
                  {feedbackToDelete.message.substring(0, 100)}...
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              onClick={handleDeleteCancel}
              variant="outline"
              disabled={isDeleting !== null}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="destructive"
              disabled={isDeleting !== null}
            >
              {isDeleting !== null ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete Feedback'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackManagementSection;